import { styled } from "@mui/material";

/**
 * Bloques de layout compartidos: contenedor, sección, encabezados de sección
 * (tag + título + bajada), texto con degradado y botones.
 */
export const StyledLayout = {
  Container: styled("div")(({ theme }) => ({
    width: "100%",
    maxWidth: theme.custom.container,
    marginInline: "auto",
    paddingInline: "clamp(20px, 5vw, 40px)",
  })),

  Section: styled("section")(({ theme }) => ({
    position: "relative",
    paddingBlock: "clamp(72px, 10vw, 128px)",
  })),

  Tag: styled("span")(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: theme.custom.fonts.display,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.custom.colors.indigo600,
    padding: "7px 14px",
    borderRadius: theme.custom.radius.full,
    background: theme.custom.gradient.brandSoft,
    border: "1px solid rgba(23, 79, 155, 0.18)",
  })),

  Title: styled("h2")(({ theme }) => ({
    fontSize: "clamp(2rem, 4.5vw, 3.1rem)",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: 20,
    maxWidth: "20ch",
  })),

  Lead: styled("p")(({ theme }) => ({
    marginTop: 18,
    fontSize: "clamp(1.02rem, 2vw, 1.18rem)",
    color: theme.custom.colors.textSoft,
    maxWidth: "62ch",
  })),

  GradientText: styled("span")(({ theme }) => ({
    background: theme.custom.gradient.brand,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  })),

  // Botón con degradado de marca (variantes: primary | ghost | light | outlineLight)
  Button: styled("a", {
    shouldForwardProp: (prop) => prop !== "variant",
  })(({ theme, variant = "primary" }) => {
    const base = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 9,
      fontFamily: theme.custom.fonts.display,
      fontWeight: 600,
      fontSize: "0.98rem",
      padding: "14px 26px",
      borderRadius: theme.custom.radius.full,
      cursor: "pointer",
      transition:
        "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease",
      whiteSpace: "nowrap",
      "& svg": { transition: "transform 0.25s ease", fontSize: "1.2rem" },
    };

    const variants = {
      primary: {
        color: "#fff",
        background: theme.custom.gradient.brand,
        boxShadow: "0 14px 30px -12px rgba(23, 79, 155, 0.7)",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 20px 40px -12px rgba(23, 79, 155, 0.85)",
        },
        "&:hover svg": { transform: "translateX(3px)" },
      },
      ghost: {
        color: theme.palette.text.primary,
        background: "rgba(255, 255, 255, 0.7)",
        border: `1px solid ${theme.custom.colors.border}`,
        backdropFilter: "blur(6px)",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "rgba(23, 79, 155, 0.4)",
          boxShadow: theme.custom.shadow.sm,
        },
      },
      light: {
        color: theme.custom.colors.navy900,
        background: "#fff",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 18px 40px -18px rgba(0,0,0,0.5)",
        },
      },
      outlineLight: {
        color: "#fff",
        border: "1px solid rgba(255, 255, 255, 0.35)",
        "&:hover": {
          background: "rgba(255,255,255,0.1)",
          transform: "translateY(-2px)",
        },
      },
    };

    return { ...base, ...variants[variant] };
  }),
};
